import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select, Switch } from "antd";
import TextArea from "antd/lib/input/TextArea";
import CustomDatePicker from "common/CustomDatePicker";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BUYER_INQUIRY_ADD_ENDPOINT,
  BUYER_INQUIRY_EDIT_ENDPOINT,
  BUYER_INQUIRY_GET_COUNTRIES_ENDPOINT,
  BUYER_INQUIRY_LIST_ENDPOINT,
  BUYER_INQUIRY_VALIDATE_STYLE_NO_ENDPOINT,
  GARMENT_CATEGORY_LIST_ENDPOINT,
  GARMENT_FOR_LIST_ENDPOINT,
  GARMENT_TYPE_LIST_ENDPOINT,
  GET_BUYING_OFFICE_ENDPOINT,
  GET_INQUIRY_NUMBER,
  GET_STYLE_LIST_ENDPOINT,
  GET_USERS_ENDPOINT,
  STYLE_ADD_DOC,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import {
  calculateExpectedOrderQuantity,
  commaSeparateNumber,
  isArrayAndHasValue,
  roundExpectedOrderQty,
} from "../../../utils/functions";
import DocumentUpload from "../../Common/DocumentUpload";
import BuyingOfficeForm from "../CreateModalUI/BuyingOfficeForm";
import BuyerForm from "../CreateModalUI/BuyerForm";
import { FABRIC_TYPE, ORDER_CHANNEL, ORDER_TYPE, disableSelectedProcess } from "../helper";
import Reference from "../Reference";
import MachineRentTable from "../SpecialProcess/MachineRentTable";
import SpecialProcessTable from "../SpecialProcess/SpecialProcessTable";
import CMForm from "./CMForm";
import OBForm from "./OBForm";
import OrderConfirmationForm from "./OrderConfirmationForm";
import { alertPop } from "common/helper/helper";

const inquiryStatus = [
  {
    id: 1,
    name: "Inquiry",
    value: "0",
  },
  {
    id: 2,
    name: "Contracted",
    value: "1",
  },
  {
    id: 3,
    name: "Closed",
    value: "2",
  },
  // {
  //   id: 4,
  //   name: "Capacity Booking",
  //   value: "3",
  // },
];

const BuyerInquiryForm = forwardRef((props, ref) => {
  // Props
  const {
    form,
    isEdit,
    editInfo,
    fileData,
    CMInfo,
    viewMode,
    users,
    setUsers,
    setStyles,
    buyingOffice,
    setBuyingOffice,
    buyer,
    setBuyer,
    brand,
    setBrand,
    garmentCategoryList,
    setGarmentCategoryList,
    garmentTypeList,
    setGarmentTypeList,
    garmentForList,
    setGarmentForList,
    countryList,
    setCountryList,
    documents,
    editMode,
    setIsLoading,
    approvalMode = false,
    approvalRedirectUrl,
  } = props;

  // States
  const [filesList, setFileList] = useState([]);
  const [selectedBuyingOffice, setSelectedBuyingOffice] = useState(null);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [subcontractStatusOption, setSubcontractStatusOption] = useState([]);
  const [specialProcessList, setSpecialProcessList] = useState(null);
  const [machineRentList, setMachineRentList] = useState(null);
  const [inquiryReferences, setInquiryReferences] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModal, setSelectedModal] = useState(null);
  const [cmFilesList, setCmFileList] = useState([]);
  const [cmFieldsChanged, setCmFieldsChanged] = useState(false);
  const [sampleStatus, setSampleStatus] = useState("No");

  // Others
  const navigate = useNavigate();
  const { Option } = Select;
  const { inquiryId } = useParams();
  const buying_office_id_watch = Form.useWatch("buying_office_id", form);
  const buyer_id_watch = Form.useWatch("buyer_id", form);
  const inquiry_status_watch = Form.useWatch("inquiry_status", form);
  const order_status_watch = Form.useWatch("order_status", form);
  const subcontract_status = Form.useWatch("subcontract_status", form);
  const is_multiple_style_watch = Form.useWatch("is_multiple_style", form);

  // Handler for CM field changes
  const handleCMFieldsChange = (hasChanged) => {
    setCmFieldsChanged(hasChanged);
  };

  const getApprovalPayloadValues = (values) => {
    if (!approvalMode || !editInfo) {
      return values;
    }

    const orderConfirmationsFromEdit = isArrayAndHasValue(editInfo?.order_confirmation)
      ? editInfo.order_confirmation.map((item) => ({
        id: item?.id || 0,
        inquiry_reason_id: item?.inquiry_reason_id || null,
        inquiry_remark_id: item?.inquiry_remark_id || null,
        is_delete: item?.is_delete || 0,
      }))
      : [];

    return {
      ...values,
      reference_id: values?.reference_id ?? editInfo?.reference_id ?? null,
      request_date:
        values?.request_date ??
        (editInfo?.request_date ? moment(editInfo.request_date) : null),
      order_confirmations: isArrayAndHasValue(values?.order_confirmations)
        ? values.order_confirmations
        : orderConfirmationsFromEdit,
      style_id: values?.style_id ?? editInfo?.style_inquiry_map?.style_info?.id ?? null,
      style_no: values?.style_no ?? editInfo?.style_inquiry_map?.style_info?.style_no ?? null,
      buying_office_id: values?.buying_office_id ?? editInfo?.buying_office_id ?? editInfo?.buying_office_info?.id ?? null,
      buyer_id: values?.buyer_id ?? editInfo?.buyer_id ?? editInfo?.buyer_info?.id ?? null,
      brand_id: values?.brand_id ?? editInfo?.brand_id ?? editInfo?.brand_info?.id ?? null,
      garment_category_id:
        values?.garment_category_id ?? editInfo?.garment_category_id ?? editInfo?.garment_category_info?.id ?? null,
      garment_type_id:
        values?.garment_type_id ?? editInfo?.garment_type_id ?? editInfo?.garment_type_info?.id ?? null,
      garment_for_id:
        values?.garment_for_id ?? editInfo?.garment_for_id ?? editInfo?.garment_for_info?.id ?? null,
      country_id: values?.country_id ?? editInfo?.country_id ?? editInfo?.country_info?.id ?? null,
      sub_contract_factory_id:
        values?.sub_contract_factory_id ?? editInfo?.sub_contract_factory_id ?? null,
      is_multiple_style:
        values?.is_multiple_style ?? (editInfo?.is_multiple_style === "Yes" ? 1 : 0),
    };
  };

  useEffect(() => {
    // When in Edit Mode, set fileList values
    if (isArrayAndHasValue(fileData)) {
      const fileInfo = fileData?.map((file) => {
        return {
          ...file,
          id: file?.id,
          name: file?.document_name,
          path: file?.document,
          link: file?.document, // For Link & Mail entries
          attachment_type: file?.attachment_type,
          reference_no: file?.reference_no,
          mail_body: file?.mail_body,
          is_delete: 0,
        };
      });

      setFileList(fileInfo);
    }
  }, [fileData]);

  const onFinish = async (values) => {
    console.log({ values });
    setIsLoading(true);

    const payloadValues = getApprovalPayloadValues(values);

    if (!approvalMode) {
      if (!isArrayAndHasValue(filesList) && !isArrayAndHasValue(values.documentLinks)) {
        // Only require attachments if sample status is "No"
        if (sampleStatus === "No" && !isArrayAndHasValue(filesList)) {
          HandleNotification("error", "Error!", "Please upload at least one document");
          setIsLoading(false);
          return;
        }
      }

      // Validate style_no prefix CGL rule
      const validationResponse = await postData(BUYER_INQUIRY_VALIDATE_STYLE_NO_ENDPOINT, {
        order_status: values?.order_status,
        style_no: values?.style_no,
        is_multiple_style: values?.is_multiple_style ? "Yes" : "No",
      });

      if (!validationResponse || validationResponse?.code !== 200) {
        setIsLoading(false);
        return;
      }
    }

    // Keep CM document upload optional for Buyer Inquiry.
    // if (!cmFilesList[0]?.id && cmFieldsChanged && isEdit) {
    //   alertPop("error", "Please upload CM document");
    //   // setIsLoading(false);
    //   // return;
    // }

    const addQuery = `${BUYER_INQUIRY_ADD_ENDPOINT}`;
    const editQuery = `${BUYER_INQUIRY_EDIT_ENDPOINT}/${inquiryId}`;
    const query = isEdit ? editQuery : addQuery;

    const sub_contract = payloadValues.subcontract_status ? {} : {};

    const bodyData = {
      ...sub_contract,
      buying_office_id: payloadValues?.buying_office_id ? Number(payloadValues?.buying_office_id) : null,
      buyer_id: payloadValues?.buyer_id ? Number(payloadValues?.buyer_id) : null,
      brand_id: payloadValues?.brand_id,
      received_date: payloadValues?.received_date ? moment(payloadValues?.received_date).format("YYYY-MM-DD") : null,
      request_date: payloadValues?.request_date ? moment(payloadValues?.request_date).format("YYYY-MM-DD") : null,
      received_by: payloadValues?.received_by,
      inquiry_no: payloadValues?.inquiry_no,
      style_no: payloadValues?.style_no,
      garment_category_id: payloadValues?.garment_category_id,
      garment_type_id: payloadValues?.garment_type_id,
      garment_for_id: payloadValues?.garment_for_id,
      inquiry_status: payloadValues?.inquiry_status,
      description: payloadValues?.description,
      order_status: payloadValues?.order_status,
      season: payloadValues?.season,
      size_range: payloadValues?.size_range,
      expected_order_quantity: payloadValues?.expected_order_quantity
        ? roundExpectedOrderQty(Number(payloadValues?.expected_order_quantity))
        : null,
      order_quantity: payloadValues?.order_quantity ? Number(payloadValues?.order_quantity) : null,
      extra_percent: payloadValues?.extra_percent ? Number(payloadValues?.extra_percent) : 0,
      expected_delivery_date: payloadValues?.expected_delivery_date
        ? moment(payloadValues?.expected_delivery_date).format("YYYY-MM-DD")
        : null,
      production_month_from: payloadValues?.production_month_from
        ? moment(payloadValues?.production_month_from).format("YYYY-MM-DD")
        : null,
      production_month_to: payloadValues?.production_month_to
        ? moment(payloadValues?.production_month_to).format("YYYY-MM-DD")
        : null,
      documents: isArrayAndHasValue(filesList)
        ? filesList?.map((file) => {
          const isLinkAndMail = file?.attachment_type === "Link & Mail";
          return {
            document_id: file?.id,
            attachment_type: file?.attachment_type || null,
            reference_no: file?.reference_no || null,
            mail_body: isLinkAndMail ? file?.mail_body || null : null,
            ...(isLinkAndMail && {
              document_name: file?.name,
              document: file?.link || null,
              document_type: "link",
            }),
            ...(!isLinkAndMail && {
              document_type: "document",
            }),
            is_delete: file?.is_delete ? 1 : 0,
          };
        })
        : [],
      order_confirmations: isArrayAndHasValue(payloadValues?.order_confirmations)
        ? payloadValues?.order_confirmations?.map((item) => {
          return {
            id: item?.id || 0,
            inquiry_reason_id: item?.inquiry_reason_id || null,
            inquiry_remark_id: item?.inquiry_remark_id || null,
            is_delete: item?.is_delete || 0,
          };
        })
        : [],
      expected_margin: payloadValues?.expected_margin,
      negotiated_cm: payloadValues?.negotiated_cm,
      total_expected_margin: payloadValues?.total_expected_margin,
      buyer_target: payloadValues?.buyer_target,
      subcontract_status: payloadValues?.subcontract_status ? 1 : 0,
      sub_contract_factory_id: payloadValues?.sub_contract_factory_id,
      order_type: payloadValues?.order_type || null,
      order_channel: payloadValues?.order_channel || null,
      fabric_content: payloadValues?.fabric_content || null,
      fob: payloadValues?.fob || null,
      capacity_availability: payloadValues?.capacity_availability || null,
      country_id: payloadValues?.country_id ? Number(payloadValues?.country_id) : null,
      embroidery: payloadValues?.embroidery ? "Yes" : "No",
      printing: payloadValues?.printing ? "Yes" : "No",
      washing: payloadValues?.washing ? "Yes" : "No",
      smoking: payloadValues?.smoking ? "Yes" : "No",
      pintuck: payloadValues?.pintuck ? "Yes" : "No",
      snap_button: payloadValues?.snap_button ? "Yes" : "No",
      is_multiple_style: payloadValues?.is_multiple_style ? "Yes" : "No",
      fabric_status: payloadValues?.fabric_status,
      trim_status: payloadValues?.trim_status,
      accessories_status: payloadValues?.accessories_status,
      reference_id: payloadValues?.reference_id,
      style_id: payloadValues?.style_id || null,
      change_cm_info: isEdit ? (cmFieldsChanged ? "Yes" : "No") : "No",
      cm_history_id: Array.isArray(cmFilesList) && cmFilesList.length > 0 ? cmFilesList[0].id : null,
      quoted_cm: payloadValues?.cgl_target ? Number(payloadValues?.cgl_target) : null,
      sample_status: approvalMode && editInfo?.sample_status ? editInfo.sample_status : sampleStatus,
      ...(approvalMode && {
        cm_status: 1,
        approval: "Yes",
        change_cm_info: "Yes",
      }),
    };

    const response = await postData(query, bodyData);

    if (response && response?.code === 200) {
      HandleNotification(
        "success",
        "Success!",
        response?.message ||
        (approvalMode
          ? "CM Value Approved Successfully"
          : `Successfully ${isEdit ? "updated" : "added"} inquiry`),
      );

      if (approvalMode) {
        navigate(approvalRedirectUrl || "/merchandising/buyer-inquiry");
      } else if (["Yes", "Projection"].includes(payloadValues.order_status) && editInfo?.ob_info && editInfo?.ob_info !== null) {
        navigate(
          `/production-planning/planningRequests/create/?id=${inquiryId}&flag=new&order_status=${encodeURIComponent(payloadValues.order_status)}`,
        );
      } else {
        navigate("/merchandising/buyer-inquiry");
      }
      setIsLoading(false);
    } else {
      const errorMsg = Array.isArray(response?.message)
        ? response.message.join(", ")
        : response?.message || `Failed to ${isEdit ? "update" : "add"} inquiry`;
      HandleNotification("error", "Error!", errorMsg);
      setIsLoading(false);
    }
  };

  const getAllUsers = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const query = `${GET_USERS_ENDPOINT}`;

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      const userId = JSON.parse(localStorage.getItem("user_id"));
      const userName = localStorage.getItem("name");

      const userData = response?.data?.data?.data || [];

      const currentUserExists = userData.some((user) => user.id === userId);

      if (!currentUserExists && userId && userName) {
        userData.unshift({
          id: userId,
          name: userName,
        });
      }

      setUsers(userData);

      if (!isEdit && !form.getFieldValue("received_by")) {
        form.setFieldsValue({
          received_by: userId,
          received_by_name: userId,
        });
      }
    }
  };

  const getAllStyles = useCallback(
    async (filterValues) => {
      const bodyData = {
        per_page: filterValues?.per_page || 20,
        ...filterValues,
      };
      const query = `${GET_STYLE_LIST_ENDPOINT}`;

      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        setStyles(response?.data?.data?.data);
      } else {
        HandleNotification("error", "Error!", response?.message || `Failed to get style list`);
      }
    },
    [setStyles],
  );

  const getAllGarmentCategory = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const query = `${GARMENT_CATEGORY_LIST_ENDPOINT}`;

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setGarmentCategoryList(response?.data?.data?.data);
    }
  };

  const getAllGarmentType = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const query = `${GARMENT_TYPE_LIST_ENDPOINT}`;

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setGarmentTypeList(response?.data?.data?.data);
    }
  };

  const getAllGarmentFor = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const query = `${GARMENT_FOR_LIST_ENDPOINT}`;

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setGarmentForList(response?.data?.data?.data);
    }
  };

  const getBuyingOfficeList = async (filterValues) => {
    const query = `${GET_BUYING_OFFICE_ENDPOINT}`;
    const bodyData = {
      per_page: 10,
      name: filterValues?.name || "",
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setBuyingOffice(response?.data?.data?.data || []);
    }
  };

  const getCountries = async (filterValues) => {
    const query = BUYER_INQUIRY_GET_COUNTRIES_ENDPOINT;
    const bodyData = {
      per_page: 500,
      status: "active",
      ...(filterValues?.name && { name: filterValues.name }),
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setCountryList(response?.data?.data?.data || []);
    }
  };

  const getBuyersList = async (value, filterValues) => {
    if (!filterValues) {
      form.resetFields(["buyer_id", "brand_id"]);
    }

    const query = `/api/buying_office/${value}/edit`;
    const bodyData = {
      ...(filterValues?.name && { name: filterValues?.name }),
      per_page: 10,
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setBuyer(response?.data?.data?.buyer || []);
    }
  };

  const getBrand = async (value, filterValues) => {
    if (!filterValues) {
      form.resetFields(["brand_id"]);
    }

    // Set buyer short name field value
    const foundBuyer = buyer?.find((item) => item?.id === value);
    form.setFieldsValue({
      buyer_short_name: foundBuyer?.short_name,
    });

    const bodyData = {
      ...(filterValues?.name && { name: filterValues?.name }),
      per_page: 10,
    };
    const response = await getData(`/api/buyers/${value}/edit`, false, bodyData);

    if (response && response?.data?.code === 200) {
      setBrand(response?.data?.data?.brands);
    }
  };

  const getSubcontractStatusOption = async (filterValues) => {
    const bodyData = {
      ...(filterValues?.name && { name: filterValues?.name }),
      per_page: 10,
    };
    const response = await getData(`/api/sub-contract-factory`, false, bodyData);

    if (response && response?.data?.code === 200) {
      setSubcontractStatusOption(response?.data?.data?.data);
    }
  };

  const getInquiryNumber = useCallback(async () => {
    // setLoading(true);

    const response = await getData(`${GET_INQUIRY_NUMBER}`);
    if (response?.data?.code === 200) {
      console.log(response);
      form.setFieldsValue({
        inquiry_no: response?.data?.data,
      });
    }
    // setLoading(false);
  }, []);

  useEffect(() => {
    if (!isEdit) {
      getInquiryNumber();
    }
  }, [getInquiryNumber, isEdit]);

  useEffect(() => {
    getAllStyles({ subcontract_status: subcontract_status ? 1 : 0 });
  }, [subcontract_status, getAllStyles]);

  useEffect(() => {
    form.setFieldsValue({
      inquiry_status: "0",
      request_date: moment(),
    });
    // Get all data on first load
    getAllUsers();
    getAllGarmentCategory();
    getAllGarmentType();
    getAllGarmentFor();
    getBuyingOfficeList();
    getCountries();
    getSubcontractStatusOption();
    getProcessMachineData();
  }, []);

  useEffect(() => {
    // In edit mode set buyer, if buying office is selected
    if (isEdit && buying_office_id_watch) {
      getBuyersList(buying_office_id_watch, {});
      setSelectedBuyingOffice(buying_office_id_watch);
    }
  }, [buying_office_id_watch, isEdit]);

  useEffect(() => {
    // In edit mode set brand, if buyer is selected
    if (isEdit && buyer_id_watch) {
      getBrand(buyer_id_watch, {});
      setSelectedBuyer(buyer_id_watch);
    }
  }, [buyer_id_watch, isEdit]);

  useEffect(() => {
    // Set sample status from editInfo when in edit mode
    if (isEdit && editInfo?.sample_status) {
      setSampleStatus(editInfo.sample_status);
    }
  }, [isEdit, editInfo]);

  useEffect(() => {
    if (isEdit && editInfo?.cm_status === 1 && isArrayAndHasValue(editInfo?.cm_histories)) {
      const latestHistoryWithDocument = editInfo.cm_histories.find(
        (history) => history?.document && history?.document_name,
      );

      if (latestHistoryWithDocument) {
        setCmFileList([
          {
            id: latestHistoryWithDocument.id,
            name: latestHistoryWithDocument.document_name,
            path: latestHistoryWithDocument.document,
            attachment_type: latestHistoryWithDocument.attachment_type,
          },
        ]);
      }
    }
  }, [isEdit, editInfo]);

  useEffect(() => {
    if (isEdit && editInfo?.sub_contract_factory_id && editInfo?.factory_info?.name) {
      setSubcontractStatusOption((oldArray) => {
        if (oldArray?.some((item) => item.id === editInfo.sub_contract_factory_id)) {
          return oldArray;
        }

        return [
          {
            id: editInfo.sub_contract_factory_id,
            name: editInfo.factory_info.name,
            address: editInfo.factory_info.address,
          },
          ...oldArray,
        ];
      });
    }
  }, [isEdit, editInfo]);

  const getProcessMachineData = useCallback(
    async (filterValues) => {
      const bodyData = {
        ...filterValues,
        per_page: filterValues?.per_page || 10,
      };
      const query = `${BUYER_INQUIRY_LIST_ENDPOINT}/${inquiryId}/view-process-machine-rent`;
      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        const processData = isArrayAndHasValue(response?.data?.data?.process_info)
          ? response?.data?.data?.process_info
          : null;
        const customProcessData =
          processData &&
          processData?.map((item, index) => {
            // Disable the once used process
            disableSelectedProcess(item?.process);

            // Return custom data
            return {
              ...item,
              is_delete: 0,
              unique_id: index,
            };
          });
        const machineData = isArrayAndHasValue(response?.data?.data?.machine_info)
          ? response?.data?.data?.machine_info
          : null;
        const customMachineData =
          machineData &&
          machineData?.map((item, index) => {
            return {
              ...item,
              is_delete: 0,
              unique_id: index,
            };
          });
        setSpecialProcessList(customProcessData);
        setMachineRentList(customMachineData);
      } else {
        HandleNotification("error", "bottomRight", "Error getting process and machine data", null);
      }
    },
    [inquiryId],
  );

  const onModalOpen = (props) => {
    const { modalName } = props;
    setSelectedModal(modalName);
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setSelectedModal(null);
  };

  const renderModalTitle = () => {
    switch (selectedModal) {
      case "buying_office":
        return "Buying Office";
      case "buyer":
        return "Buyer";
      case "fabric_type":
        return "Fabric Type";
      default:
        return "";
    }
  };

  const handleBuyerCreated = (newBuyer) => {
    if (newBuyer?.id) {
      setBuyer((prev) => {
        const list = prev || [];
        if (list.some((item) => item.id === newBuyer.id)) {
          return list;
        }
        return [...list, newBuyer];
      });
      form.setFieldsValue({
        buyer_id: newBuyer.id,
        buyer_short_name: newBuyer.short_name,
      });
      setSelectedBuyer(newBuyer.id);
    }
  };

  const renderModalUI = () => {
    switch (selectedModal) {
      case "buying_office":
        return <BuyingOfficeForm onCloseModal={onCloseModal} refetch={getBuyingOfficeList} />;
      case "buyer":
        return (
          <BuyerForm onCloseModal={onCloseModal} onBuyerCreated={handleBuyerCreated} />
        );
      case "fabric_type":
        return "Fabric Type";
      default:
        return null;
    }
  };

  return (
    <div>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{
          order_status: "Waiting For Feedback",
          washing: true,
          snap_button: true,
        }}
      >
        {approvalMode && (
          <>
            <Form.Item name="reference_id" hidden />
            <Form.Item name="order_confirmations" hidden />
          </>
        )}
        <Card>
          <Row gutter={6}>
            <Col span={4}>
              <Form.Item label=" " name="subcontract_status" valuePropName="checked">
                <Checkbox style={{ fontWeight: 'bold', fontSize: '24px' }} disabled={viewMode || editMode}>Sub-Contract</Checkbox>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label=" " name="is_multiple_style" valuePropName="checked" initialValue={false}>
                <Checkbox style={{ fontWeight: 'bold', fontSize: '24px' }} disabled={viewMode || editMode}>Multi Style</Checkbox>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Inquiry Status"
                name="inquiry_status"
                rules={[
                  {
                    required: true,
                    message: "Inquiry Status is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select status"
                  optionFilterProp="children"
                  disabled={true}
                  size="small"
                  allowClear
                >
                  {isArrayAndHasValue(inquiryStatus) &&
                    inquiryStatus?.map((statusItem, index) => (
                      <Option value={statusItem?.value} key={statusItem?.id || index}>
                        {statusItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Inquiry No."
                name="inquiry_no"
                disabled={true}
                rules={[
                  {
                    required: true,
                    message: "Inquiry No. is required",
                  },
                ]}
              >
                <Input placeholder="Inquiry No." size="small" disabled={true} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Style No."
                name="style_no"
                rules={[
                  {
                    required: !is_multiple_style_watch,
                    message: "Style No. is required",
                  },
                ]}
              >
                <Input placeholder="Style No." size="small" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Received Date" name="received_date" initialValue={moment()}>
                <CustomDatePicker align="right" size="small" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Request Date" name="request_date" initialValue={moment()}>
                <CustomDatePicker align="right" size="small" disabled={true} />
              </Form.Item>
            </Col>
            {/* here I want to auto select merchandiser from user */}
            <Col span={4}>
              <Form.Item name="received_by" hidden />
              <Form.Item label="Merchandiser" name="received_by_name">
                <Select
                  showSearch
                  placeholder="Select user"
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  onSearch={(value) => getAllUsers({ name: value })}
                  allowClear
                  onSelect={(value) => {
                    form.setFieldsValue({
                      received_by: value,
                    });
                  }}
                >
                  {isArrayAndHasValue(users) &&
                    users?.map((userItem, index) => (
                      <Option value={userItem?.id} key={userItem?.id || index}>
                        {userItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            {subcontract_status ? (
              <>
                <Col span={4}>
                  <Form.Item label="Factory Name" name="sub_contract_factory_id">
                    <Select
                      showSearch
                      placeholder="Select a Factory Name"
                      disabled={viewMode}
                      allowClear
                      size="small"
                      onChange={(value) => {
                        const selectedFactory = subcontractStatusOption.find((item) => item.id === value);
                        form.setFieldsValue({
                          address: selectedFactory?.address,
                        });
                      }}
                    >
                      {isArrayAndHasValue(subcontractStatusOption) &&
                        subcontractStatusOption.map((sup) => (
                          <Option value={sup?.id} key={sup?.id}>
                            {sup.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Address" name="address">
                    <Input placeholder="Address" size="small" disabled />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <>
                <Col span={4}>
                  <Row gutter={10}>
                    <Col span={20}>
                      <Form.Item
                        label="Buying office"
                        name="buying_office_id"
                        rules={[
                          {
                            required: true,
                            message: `Buying office is required `,
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          style={{
                            width: "100%",
                          }}
                          placeholder="Select a Buying office"
                          onChange={(value) => {
                            if (value) {
                              getBuyersList(value);
                              setSelectedBuyingOffice(value);
                            } else {
                              form.resetFields(["buyer_id", "brand_id"]);
                              setBuyer([]);
                              setBrand([]);
                              setSelectedBuyingOffice(null);
                            }
                          }}
                          optionFilterProp="children"
                          disabled={viewMode}
                          size="small"
                          onSearch={(value) => {
                            getBuyingOfficeList({ name: value });
                          }}
                          allowClear
                          onClear={() => getBuyingOfficeList()}
                        >
                          {isArrayAndHasValue(buyingOffice) &&
                            buyingOffice.map((sup) => (
                              <Option value={sup?.id} key={sup?.id}>
                                {sup?.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <div className="centered-item-parent">
                        <Form.Item label=" ">
                          <Button
                            icon={<PlusOutlined />}
                            // size="small"
                            onClick={() => onModalOpen({ modalName: "buying_office" })}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col span={4}>
                  <Form.Item name="buyer_short_name" hidden />
                  <Row gutter={10}>
                    <Col span={20}>
                      <Form.Item
                        label="Buyer"
                        name="buyer_id"
                        rules={[{ required: true, message: `Buyer is required ` }]}
                      >
                        <Select
                          showSearch
                          placeholder="Select a Buyer"
                          onChange={(value) => {
                            if (value) {
                              getBrand(value);
                              setSelectedBuyer(value);
                            } else {
                              form.resetFields(["brand_id"]);
                              setBrand([]);
                              setSelectedBuyer(null);
                            }
                          }}
                          optionFilterProp="children"
                          disabled={viewMode}
                          size="small"
                          onSearch={(value) => getBuyersList(selectedBuyingOffice, { name: value })}
                          allowClear
                          onClear={() => getBuyersList(selectedBuyingOffice)}
                        >
                          {isArrayAndHasValue(buyer) &&
                            buyer.map((buy) => (
                              <Option value={buy?.id} key={buy?.id}>
                                {buy?.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <div className="centered-item-parent">
                        <Form.Item label=" ">
                          <Button
                            icon={<PlusOutlined />}
                            disabled={viewMode || !selectedBuyingOffice}
                            onClick={() => onModalOpen({ modalName: "buyer" })}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col span={4}>
                  <Form.Item label="Brand" name="brand_id">
                    <Select
                      showSearch
                      placeholder="Select a Brand"
                      optionFilterProp="children"
                      onSearch={(value) => getBrand(selectedBuyer, { name: value })}
                      disabled={viewMode}
                      size="small"
                      allowClear
                      onClear={() => setBrand([])}
                    >
                      {brand?.length &&
                        brand?.map((pi) => (
                          <Option value={pi?.id} key={pi?.id}>
                            {pi?.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )}
            <Col span={4}>
              <Form.Item label="Garments Category" name="garment_category_id">
                <Select
                  showSearch
                  placeholder="Select a category"
                  optionFilterProp="children"
                  size="small"
                  disabled={viewMode}
                  onSearch={(value) => getAllGarmentCategory({ name: value })}
                  allowClear
                >
                  {isArrayAndHasValue(garmentCategoryList) &&
                    garmentCategoryList?.map((categoryItem, index) => (
                      <Option value={categoryItem?.id} key={categoryItem?.id || index}>
                        {categoryItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Garments Type" name="garment_type_id">
                <Select
                  showSearch
                  placeholder="Select a type"
                  optionFilterProp="children"
                  size="small"
                  disabled={viewMode}
                  onSearch={(value) => getAllGarmentType({ name: value })}
                  allowClear
                >
                  {isArrayAndHasValue(garmentTypeList) &&
                    garmentTypeList?.map((typeItem, index) => (
                      <Option value={typeItem?.id} key={typeItem?.id || index}>
                        {typeItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Garments For" name="garment_for_id">
                <Select
                  showSearch
                  placeholder="Select for"
                  optionFilterProp="children"
                  size="small"
                  onSearch={(value) => getAllGarmentFor({ name: value })}
                  allowClear
                  disabled={viewMode}
                >
                  {isArrayAndHasValue(garmentForList) &&
                    garmentForList?.map((typeItem, index) => (
                      <Option value={typeItem?.id} key={typeItem?.id || index}>
                        {typeItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Order Qty."
                name="order_quantity"
                rules={[
                  {
                    required: true,
                    message: `Order Qty. is required `,
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Order Qty."
                  disabled={viewMode}
                  formatter={(value) => commaSeparateNumber(value)}
                  onChange={() => {
                    setTimeout(() => {
                      const orderQty = form.getFieldValue("order_quantity");
                      const percentage = form.getFieldValue("extra_percent") || 0;
                      form.setFieldsValue({
                        expected_order_quantity: calculateExpectedOrderQuantity(orderQty, percentage),
                      });
                    }, 0);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Cutting (%)"
                name="extra_percent"
                rules={[
                  {
                    required: true,
                    message: "Cutting(%) is required",
                  },
                  {
                    validator: (rule, value, callback) => {
                      if (value > 100) {
                        callback("Cutting(%) cannot be greater than 100");
                      }
                      callback();
                    },
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Cutting (%)"
                  disabled={viewMode}
                  min={0}
                  max={100}
                  onChange={() => {
                    setTimeout(() => {
                      const orderQty = form.getFieldValue("order_quantity");
                      const percentage = form.getFieldValue("extra_percent") || 0;
                      form.setFieldsValue({
                        expected_order_quantity: calculateExpectedOrderQuantity(orderQty, percentage),
                      });
                    }, 0);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Exp. Order Qty. (Calculated)" name="expected_order_quantity">
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Calculated Qty."
                  disabled={true}
                  precision={0}
                  formatter={(value) => commaSeparateNumber(value)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Exp. Delivery Date" name="expected_delivery_date">
                <CustomDatePicker align="right" size="small" disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Production Month From"
                name="production_month_from"
                rules={[
                  {
                    required: true,
                    message: "Production Month From is required",
                  }
                ]}
              // rules={[
              //   {
              //     required:
              //       inquiry_status_watch === "3" ||
              //       order_status_watch === "Waiting For Feedback" ||
              //       order_status_watch === "Yes" ||
              //       order_status_watch === "Projection",
              //     message: "Production Month From is required",
              //   },
              // ]}
              >
                <CustomDatePicker
                  align="right"
                  size="small"
                  disabled={viewMode}
                // picker="month"
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Production Month To"
                name="production_month_to"
                rules={[
                  {
                    required: true,
                    message: "Production Month To is required",
                  }
                ]}
              // rules={[
              //   {
              //     required:
              //       inquiry_status_watch === "3" ||
              //       order_status_watch === "Yes" ||
              //       order_status_watch === "Projection",
              //     message: "Production Month To is required",
              //   },
              // ]}
              >
                <CustomDatePicker
                  align="right"
                  size="small"
                  disabled={viewMode}
                // picker="month"
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Description"
                name="description"
                rules={[{ required: true, message: `Description is required ` }]}
              >
                <TextArea size="small" placeholder="Description" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Month"
                name="season"
                normalize={(value) => {
                  if (value === "" || value === null || value === undefined) {
                    return null;
                  }
                  const num = Number(value);
                  return Number.isNaN(num) ? null : Math.trunc(num);
                }}
                rules={[
                  {
                    // required: order_status_watch === "Waiting For Feedback",
                    required: true,
                    message: "Month is required",
                  },
                  {
                    validateTrigger: ["onChange", "onBlur"],
                    validator: (_, value) => {
                      if (value === null || value === undefined || value === "") {
                        return Promise.resolve();
                      }
                      const num = Number(value);
                      if (Number.isInteger(num)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Month must be an integer"));
                    },
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Month"
                  disabled={viewMode}
                  precision={0}
                  controls={false}
                  parser={(value) => {
                    const cleaned = String(value ?? "").replace(/\D/g, "");
                    return cleaned === "" ? "" : Number(cleaned);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Size Range" name="size_range">
                <Input style={{ width: "100%" }} size="small" placeholder="Size Range" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Order Type" name="order_type">
                <Select
                  showSearch
                  placeholder="Select order type"
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  allowClear
                >
                  {isArrayAndHasValue(ORDER_TYPE) &&
                    ORDER_TYPE?.map((item, index) => (
                      <Option value={item?.value} key={item?.id || index}>
                        {item?.label || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Order Channel" name="order_channel">
                <Select
                  showSearch
                  placeholder="Select order channel"
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  allowClear
                >
                  {isArrayAndHasValue(ORDER_CHANNEL) &&
                    ORDER_CHANNEL?.map((item, index) => (
                      <Option value={item?.value} key={item?.id || index}>
                        {item?.label || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={`Fabric ${subcontract_status ? "Content" : "Type"}`} name="fabric_content">
                <Select
                  showSearch
                  placeholder={`Select Fabric ${subcontract_status ? "Content" : "Type"}`}
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  allowClear
                >
                  {isArrayAndHasValue(FABRIC_TYPE) &&
                    FABRIC_TYPE?.map((item, index) => (
                      <Option value={item?.value} key={item?.id || index}>
                        {item?.label || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={subcontract_status ? 0 : 4}>
              <Form.Item label="FOB $" name="fob" hidden={subcontract_status}>
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="FOB"
                  disabled={viewMode}
                  formatter={(value) => commaSeparateNumber(value)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Capacity Availability"
                name="capacity_availability"
                rules={[
                  {
                    required: false,
                    message: `Capacity Availability is required `,
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Capacity Availability"
                  disabled={viewMode}
                  formatter={(value) => commaSeparateNumber(value)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Country"
                name="country_id"
                rules={[
                  {
                    required: order_status_watch === "Waiting For Feedback",
                    message: "Country is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select Country"
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  allowClear
                  onSearch={(value) => getCountries({ name: value })}
                  onClear={() => getCountries()}
                  filterOption={false}
                >
                  {isArrayAndHasValue(countryList) &&
                    countryList?.map((item) => (
                      <Option value={item?.id} key={item?.id}>
                        {item?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={subcontract_status ? 4 : 0}>
              <Form.Item label="Fabric Status" name="fabric_status" hidden={!subcontract_status}>
                <Input style={{ width: "100%" }} size="small" placeholder="Fabric Status" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={subcontract_status ? 4 : 0}>
              <Form.Item label="Trims Status" name="trim_status" hidden={!subcontract_status}>
                <Input style={{ width: "100%" }} size="small" placeholder="Trims Status" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={subcontract_status ? 4 : 0}>
              <Form.Item label="Accessories Status" name="accessories_status" hidden={!subcontract_status}>
                <Input style={{ width: "100%" }} size="small" placeholder="Accessories Status" disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                name="embroidery"
                placeholder="Embroidery"
                label="Embroidery"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="printing"
                placeholder="Printing"
                label="Printing"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="washing"
                placeholder="Washing"
                label="Washing"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null) {
                        return Promise.reject(new Error("Washing is required"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="smoking"
                placeholder="Smocking"
                label="Smocking"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="pintuck"
                placeholder="Pintuck"
                label="Pintuck"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="snap_button"
                placeholder="Snap Button"
                label="Snap Button"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null) {
                        return Promise.reject(new Error("Snap Button is required"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>

          </Row>
        </Card>

        {!approvalMode && (
          <>
            <Reference
              inquiryReferences={inquiryReferences}
              setInquiryReferences={setInquiryReferences}
              viewMode={viewMode}
            />

            <SpecialProcessTable view={true} specialProcessList={specialProcessList} />

            <MachineRentTable view={true} machineRentList={machineRentList} />

            <Card
              className="custome-table"
              title={null}
            >
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "14px" }}>Sample:</span>
                    <Switch
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                      checked={sampleStatus === "Yes"}
                      onChange={(checked) => setSampleStatus(checked ? "Yes" : "No")}
                      disabled={viewMode}
                    />
                  </div>
                </Col>
              </Row>
            </Card>

            {sampleStatus === "No" && (
              <Row gutter={16}>
                <Col span={24}>
                  {sampleStatus === "No" && (
                    <DocumentUpload
                      DOC_ADD={STYLE_ADD_DOC}
                      setFileList={setFileList}
                      filesList={filesList}
                      view={viewMode}
                      header={
                        viewMode ? null : (
                          <span style={{ fontSize: "14px" }}>
                            Documents <span style={{ color: "red" }}>*</span>
                          </span>
                        )
                      }
                      preview={true}
                      showType={true}
                      showReference={true}
                      excludeSample={true}
                      showLink={true}
                    />
                  )}

                  {Array.isArray(documents) && documents.length > 0 && (
                    <DocumentUpload
                      filesList={documents}
                      view={true}
                      preview={false}
                      showType={true}
                      showReference={true}
                      showLink={true}
                    />
                  )}
                </Col>
              </Row>
            )}

            {/* Document Links Form List moved to below */}
          </>
        )}

        {isEdit && (
          <>
            <OBForm editInfo={editInfo} />
            {(approvalMode || editInfo?.initial_sam_status === "Approved") && (
              <CMForm
                CMInfo={CMInfo}
                inquiryId={inquiryId}
                viewMode={viewMode}
                approvalMode={approvalMode}
                form={form}
                histories={editInfo?.cm_histories}
                filesList={cmFilesList}
                setFileList={setCmFileList}
                onCMFieldsChange={handleCMFieldsChange}
                editInfo={editInfo}
                cmFieldsChanged={cmFieldsChanged}
              />
            )}
            {!approvalMode && <OrderConfirmationForm form={form} viewMode={viewMode} />}
          </>
        )}

        {/* <Card className="custome-table" title={<span style={{ fontSize: "14px" }}>Document Links</span>}>
          <Form.List name="documentLinks">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, fieldKey, ...restField }) => (
                  <Row gutter={16} key={key} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={10}>
                      <Form.Item
                        {...restField}
                        name={[name, "name"]}
                        fieldKey={[fieldKey, "name"]}
                        rules={[{ required: true, message: "Please input document name" }]}
                      >
                        <Input placeholder="Document Name" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item
                        {...restField}
                        name={[name, "link"]}
                        fieldKey={[fieldKey, "link"]}
                        rules={[{ required: true, message: "Please input document link" }]}
                      >
                        <Input placeholder="Document Link" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button type="link" danger onClick={() => remove(name)} disabled={viewMode}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} disabled={viewMode}>
                    Add Document Link
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Card> */}
      </Form>

      <Modal
        open={isModalOpen}
        title={`Create ${renderModalTitle()}`}
        width={selectedModal === "buyer" ? 1000 : 520}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        {renderModalUI()}
      </Modal>
    </div >
  );
});

export default BuyerInquiryForm;
